/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import {
  Brain, RefreshCw, AlertTriangle, Loader2, Search,
  ArrowRight, Activity, Power, BellRing,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const GATE_META = {
  green:      { label: 'Green',      cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30' },
  yellow:     { label: 'Yellow',     cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' },
  red:        { label: 'Red',        cls: 'bg-rose-500/15 text-rose-200 border-rose-500/30' },
  severe_red: { label: 'Severe red', cls: 'bg-rose-700/30 text-rose-100 border-rose-700/60' },
};

const STATUS_META = {
  active:    { label: 'Active',    cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30' },
  paused:    { label: 'Paused',    cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' },
  draft:     { label: 'Draft',     cls: 'bg-white/5 text-white/60 border-white/10' },
  archived:  { label: 'Archived',  cls: 'bg-white/5 text-white/40 border-white/10' },
};

export default function NeuroFlowOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [search, setSearch] = useState('');
  const [findResults, setFindResults] = useState([]);
  const [findLoading, setFindLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(`${API}/neuroflow/overview`);
      setRows(r.data?.items || []);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load NeuroFlow overview');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Search subscribers (existing /api/users endpoint)
  useEffect(() => {
    let cancelled = false;
    if (search.trim().length < 2) {
      setFindResults([]);
      return () => { cancelled = true; };
    }
    const t = setTimeout(async () => {
      setFindLoading(true);
      try {
        const r = await axios.get(`${API}/users?role=subscriber`);
        if (cancelled) return;
        const q = search.trim().toLowerCase();
        const filtered = (r.data?.users || r.data || [])
          .filter(u =>
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            (u.id || '').toLowerCase().includes(q),
          )
          .slice(0, 12);
        setFindResults(filtered);
      } catch {
        if (!cancelled) setFindResults([]);
      } finally {
        if (!cancelled) setFindLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  const filteredActive = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.subscriber_name || '').toLowerCase().includes(q) ||
      (r.subscriber_id || '').toLowerCase().includes(q) ||
      (r.active_layer_name || '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  if (!user) return null;

  return (
    <AdminLayout>
      <div
        data-testid="neuroflow-overview-page"
        className="px-6 py-6 max-w-7xl mx-auto"
      >
        <div className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <Brain className="w-6 h-6 text-violet-300" />
            <h1 className="text-2xl font-semibold text-white">
              NeuroFlow™ — PD Calibration
            </h1>
            <Badge className="bg-violet-500/15 text-violet-200 border-violet-500/30 text-[10px]">
              Phase C • Admin UI
            </Badge>
          </div>
          <p className="text-sm text-white/50">
            Dynamic Parkinson execution & L-Dopa response scheduler. Module is
            generic — admin must select any subscriber and activate manually.
          </p>
        </div>

        {/* Search */}
        <div className="rounded-lg border border-white/10 bg-slate-900/40 p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-white/40" />
            <input
              data-testid="neuroflow-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find any subscriber by name, email or ID…"
              className="flex-1 bg-transparent border-b border-white/10 text-white/90 text-sm placeholder-white/30 focus:outline-none focus:border-cyan-500/40 py-1"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="border-white/15 text-white/70 h-8 text-xs"
              data-testid="neuroflow-overview-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          {search.trim().length >= 2 && (
            <div data-testid="neuroflow-search-results">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">
                Subscriber search
              </div>
              {findLoading && (
                <div className="text-xs text-white/40 py-2">Searching…</div>
              )}
              {!findLoading && findResults.length === 0 && (
                <div className="text-xs text-white/40 py-2">No subscribers match.</div>
              )}
              {!findLoading && findResults.length > 0 && (
                <ul className="divide-y divide-white/5 max-h-64 overflow-auto">
                  {findResults.map((u) => (
                    <li
                      key={u.id}
                      data-testid={`neuroflow-search-row-${u.id}`}
                      className="py-2 px-1 flex items-center gap-3 hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => navigate(`/admin/neuroflow/${u.id}`)}
                    >
                      <div className="flex-1">
                        <div className="text-sm text-white/90">{u.name || u.email}</div>
                        <div className="text-[11px] text-white/40 font-mono">
                          {u.email} · {u.id}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-violet-500/30 text-violet-200 h-7 text-xs"
                      >
                        Open
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Active modules table */}
        <div className="mb-2 flex items-center gap-2">
          <Activity className="w-4 h-4 text-white/40" />
          <h2 className="text-sm font-medium text-white/80 uppercase tracking-wider">
            Activated subscribers
          </h2>
          <span className="text-xs text-white/40">({filteredActive.length})</span>
        </div>

        {loading && (
          <div className="rounded border border-white/10 bg-slate-900/40 p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
          </div>
        )}
        {pageError && !loading && (
          <div
            data-testid="neuroflow-overview-error"
            className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center"
          >
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
          </div>
        )}
        {!loading && !pageError && filteredActive.length === 0 && (
          <div
            data-testid="neuroflow-overview-empty"
            className="rounded-lg border border-white/10 bg-slate-900/40 p-10 text-center"
          >
            <Power className="w-8 h-8 mx-auto text-white/30 mb-3" />
            <div className="text-sm text-white/80 mb-1">No active NeuroFlow modules.</div>
            <div className="text-xs text-white/50">
              Search a subscriber above to activate the module manually.
            </div>
          </div>
        )}
        {!loading && !pageError && filteredActive.length > 0 && (
          <div
            data-testid="neuroflow-overview-rows"
            className="rounded-lg border border-white/10 bg-slate-900/40 overflow-hidden"
          >
            <table className="w-full text-xs">
              <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Subscriber</th>
                  <th className="text-left px-3 py-2 font-medium">Module</th>
                  <th className="text-left px-3 py-2 font-medium">Active layer</th>
                  <th className="text-left px-3 py-2 font-medium">Last gate</th>
                  <th className="text-left px-3 py-2 font-medium">Last snapshot</th>
                  <th className="text-left px-3 py-2 font-medium">Open alerts</th>
                  <th className="text-right px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredActive.map((r) => (
                  <OverviewRow key={r.subscriber_id} row={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function OverviewRow({ row }) {
  const sm = STATUS_META[row.module_status] || STATUS_META.draft;
  const gm = GATE_META[row.last_gate_status];
  const testId = `neuroflow-row-${row.subscriber_id}`;
  return (
    <tr
      data-testid={testId}
      className="border-b border-white/5 hover:bg-white/[0.02] align-top"
    >
      <td className="px-3 py-2">
        <div className="text-white/90">{row.subscriber_name}</div>
        <div className="text-[10px] font-mono text-white/40">{row.subscriber_id}</div>
      </td>
      <td className="px-3 py-2">
        <Badge className={`text-[10px] ${sm.cls}`}>{sm.label}</Badge>
      </td>
      <td className="px-3 py-2">
        <div className="text-white/80">{row.active_layer_name || '—'}</div>
        {row.active_layer_code && (
          <div className="text-[10px] font-mono text-white/40">{row.active_layer_code}</div>
        )}
      </td>
      <td className="px-3 py-2">
        {gm ? <Badge className={`text-[10px] ${gm.cls}`}>{gm.label}</Badge> : <span className="text-white/40">—</span>}
      </td>
      <td className="px-3 py-2 text-white/70 font-mono text-[11px]">
        {row.last_snapshot_date || '—'}
      </td>
      <td className="px-3 py-2">
        {row.open_alert_count > 0 ? (
          <Badge className="bg-rose-500/15 text-rose-200 border-rose-500/30 text-[10px]">
            <BellRing className="w-3 h-3 mr-1" /> {row.open_alert_count}
          </Badge>
        ) : (
          <span className="text-white/40 text-[11px]">0</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <Link to={`/admin/neuroflow/${row.subscriber_id}`} data-testid={`${testId}-open`}>
          <Button
            size="sm"
            variant="outline"
            className="border-violet-500/30 text-violet-200 hover:bg-violet-500/10 h-7 text-xs"
          >
            Open NeuroFlow
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </td>
    </tr>
  );
}
